import React, { memo } from 'react';
import { Box, Checkbox, Divider, Paper, Stack, Typography } from '@mui/material';
import type { IdCardAudience, IdCardField } from '../../../types/idCards';
import { FIELDS } from '../catalog';
import { MoveButtons, moveAt } from '../../admissions/settings/sectionParts';
import type { CardPerson } from '../cardPeople';

interface Props {
  audience: IdCardAudience;
  value: IdCardField[];
  onChange: (fields: IdCardField[]) => void;
  max: number;
  /** The first detail is shown as a badge (sidebar and portrait layouts). */
  featuresFirst: boolean;
  /** Shown beside each detail, so staff see what it prints. */
  person: CardPerson;
}

/** The details on the front: tick to show, arrows to order. */
const FieldPicker: React.FC<Props> = ({ audience, value, onChange, max, featuresFirst, person }) => {
  const shown = value.flatMap(k => FIELDS[audience].filter(f => f.key === k));
  const hidden = FIELDS[audience].filter(f => !value.includes(f.key));
  const full = value.length >= max;
  const rows = [...shown, ...hidden];

  return (
    <Stack spacing={1}>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {rows.map((f, i) => {
          const index = value.indexOf(f.key);
          const on = index >= 0;
          return (
            <React.Fragment key={f.key}>
              {i > 0 && <Divider sx={i === shown.length ? { borderBottomWidth: 2 } : undefined} />}
              <Stack direction="row" alignItems="center" sx={{ pl: 0.5, pr: 1, py: 0.25, opacity: !on && full ? 0.55 : 1 }}>
                <Checkbox
                  size="small"
                  checked={on}
                  disabled={!on && full}
                  onChange={() => onChange(on ? value.filter(k => k !== f.key) : [...value, f.key])}
                  inputProps={{ 'aria-label': `Show ${f.label}` }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="baseline">
                    <Typography variant="body2" fontWeight={on ? 600 : 400}>{f.label}</Typography>
                    {on && index === 0 && featuresFirst && (
                      <Typography variant="caption" color="primary.main" fontWeight={600}>Featured</Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ fontFamily: f.mono ? 'monospace' : undefined }}>
                    {person.values[f.key] || '—'}
                  </Typography>
                </Box>
                {on && <MoveButtons index={index} count={value.length} onMove={dir => onChange(moveAt(value, index, dir))} />}
              </Stack>
            </React.Fragment>
          );
        })}
      </Paper>
      <Typography variant="caption" color="text.secondary">
        {value.length} of {max} shown{full ? ' — this layout has no room for more' : ''}.
        {featuresFirst ? ' The first is featured under the photo.' : ''}
      </Typography>
    </Stack>
  );
};

export default memo(FieldPicker);
